import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Check,
  Dumbbell,
  Lock,
  PlayCircle,
  Salad,
  Sparkles,
  Utensils,
} from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL || "https://api.mogym.ir";

export default function ProgramPreviewPage() {
  const navigate = useNavigate();
  const location = useLocation();
const [videoModal, setVideoModal] = useState(false);
const [selectedVideo, setSelectedVideo] = useState(null);
  const form =
    location?.state?.form ||
    safeJsonParse(localStorage.getItem("campaignWizardForm"));

  const analysis =
    location?.state?.analysis ||
    safeJsonParse(localStorage.getItem("campaignAnalysis"));

  const mobileNumber =
    location?.state?.mobileNumber ||
    localStorage.getItem("campaignMobileNumber") ||
    "";

  const planGuid =
    location?.state?.planGuid ||
    localStorage.getItem("campaignPlanGuid") ||
    "";

  const token =
    location?.state?.token ||
    localStorage.getItem("campaignToken") ||
    "";

  const [preview, setPreview] = useState(
    safeJsonParse(localStorage.getItem("campaignPreview"))
  );
  const [loading, setLoading] = useState(!preview);
  const [error, setError] = useState("");

useEffect(() => {
  if (!planGuid || !token) {
    navigate("/body-analysis/otp");
    return;
  }

  if (preview) return;

  loadPreview();
}, [planGuid, token, preview]);

  const loadPreview = async () => {
    try {
      setLoading(true);
      setError("");

      const res = await fetch(`${API_URL}/api/campaign/body-analysis/preview`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          Id: planGuid
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.message || data?.Message || "خطا در دریافت پیش‌نمایش برنامه");
      }

      setPreview(data);
      localStorage.setItem("campaignPreview", JSON.stringify(data));
    } catch (e) {
      setError(e?.message || "خطا در دریافت پیش‌نمایش برنامه");
    } finally {
      setLoading(false);
    }
  };

  const hero = preview?.heroSummary;
  const workoutDay = preview?.workoutPreviewDay;
  const nutritionDay = preview?.nutritionPreviewDay;
  const lockedSections = preview?.lockedSections || [];

  const miniValues = useMemo(() => {
    return {
      title: preview?.priceSummary?.requestedPlanLabel || hero?.title || "برنامه اختصاصی",
      engine: preview?.engine === "rule-based" ? "هوشمند" : preview?.engine || "هوشمند",
      difficulty: workoutDay?.difficulty || "شخصی‌سازی‌شده",
    };
  }, [preview, hero, workoutDay]);

  if (loading) {
    return (
      <Shell>
        <section className="relative z-10 mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-5 text-center">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-emerald-400/20 blur-3xl" />
            <div className="relative grid h-28 w-28 place-items-center rounded-[34px] border border-emerald-300/20 bg-black/35 text-emerald-300 shadow-[0_0_80px_rgba(52,211,153,.24)] backdrop-blur-2xl">
              <Sparkles size={42} className="animate-pulse" />
            </div>
          </div>

          <h1 className="mt-8 text-2xl font-black">در حال ساخت پیش‌نمایش برنامه...</h1>

          <p className="mt-4 max-w-xs text-sm leading-7 text-white/60">
            چند لحظه صبر کن؛ موجیم داره بخشی از برنامه اختصاصی تو رو آماده می‌کنه.
          </p>
        </section>
      </Shell>
    );
  }

  if (error) {
    return (
      <Shell>
        <section className="relative z-10 mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-5 text-center">
          <div className="rounded-3xl border border-red-400/20 bg-red-500/10 p-5 text-red-200">
            {error}
          </div>

          <button
            onClick={loadPreview}
            className="mt-5 h-14 w-full rounded-2xl bg-emerald-400 font-black text-black"
          >
            تلاش دوباره
          </button>
        </section>
      </Shell>
    );
  }

  return (
    <Shell>
      <section className="relative z-10 mx-auto flex h-screen max-w-md flex-col px-4 pt-5 pb-[104px]">
        <header className="flex items-center justify-between">
          <img src="/logo.png" alt="MOGYM" className="h-10 w-auto" />

          <div className="rounded-full border border-emerald-400/20 bg-emerald-400/10 px-3 py-2 text-[11px] text-emerald-100 backdrop-blur-2xl">
            پیش‌ نمایش آماده شد
          </div>
        </header>

        <div className="mt-6">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-[30px] border border-emerald-400/20 bg-emerald-400/10 p-5 shadow-2xl backdrop-blur-2xl"
          >
            <div className="flex items-center gap-3">
              <div className="grid h-13 w-13 place-items-center rounded-3xl bg-emerald-300 text-black shadow-[0_0_50px_rgba(52,211,153,.42)]">
                <Sparkles size={28} />
              </div>

              <div>
                <h1 className="text-md font-black">
                  {hero?.title || "برنامه‌ای که موجیم برای تو طراحی کرده"}
                </h1>
                <p className="mt-1 text-xs leading-6 text-white/60">
                  {hero?.subtitle || "بخشی از برنامه باز شده؛ نسخه کامل بعد از فعال‌ سازی نمایش داده میشه"}
                </p>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-2">
              <Mini label="نوع برنامه" value={miniValues.title} />
              <Mini label="موتور" value={miniValues.engine} />
              <Mini label="سطح" value={miniValues.difficulty} />
            </div>

            {(hero?.highlights || []).length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {hero.highlights.slice(0, 3).map((item) => (
                  <span
                    key={item}
                    className="rounded-full border border-white/10 bg-black/25 px-3 py-2 text-[11px] text-white/75"
                  >
                    {item}
                  </span>
                ))}
              </div>
            )}
          </motion.div>
        </div>

        <div className="mt-5 min-h-0 flex-1 overflow-y-auto overscroll-contain pb-4 pr-1 scrollbar-hide">
          {workoutDay && 
          <WorkoutPreview
    workoutDay={workoutDay}
    onPlay={(exercise) => {
        setSelectedVideo(exercise);
        setVideoModal(true);
    }}/>}
          {nutritionDay && <MealPreview nutritionDay={nutritionDay} />}
          <LockedValue lockedSections={lockedSections} disclaimer={preview?.meta?.disclaimer} />
        </div>
      </section>

      <div className="fixed bottom-4 left-0 right-0 z-40 px-4">
        <div className="mx-auto max-w-md rounded-[26px] border border-white/10 bg-black/45 p-3 shadow-2xl backdrop-blur-2xl">
          <button
            onClick={() =>
              navigate("/body-analysis/offer", {
                state: {
                  form,
                  analysis,
                  preview,
                  mobileNumber,
                  planGuid,
                  token,
                },
              })
            }
            className="flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-300 via-green-400 to-cyan-400 font-black text-black shadow-[0_0_55px_rgba(52,211,153,.45)]"
          >
            <ArrowRight size={19} />
            {preview?.cta?.primaryLabel || "فعال‌ سازی برنامه کامل من"}
          </button>
        </div>
      </div>

            {videoModal && (
        <div
          className="fixed inset-0 z-[999] flex items-center justify-center bg-black/80 backdrop-blur-md"
          onClick={() => setVideoModal(false)}
        >
          <div
            className="relative w-[92%] max-w-md overflow-hidden rounded-3xl border border-white/10 bg-[#111827]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-white/10 p-4">
              <div>
                <div className="font-bold">{selectedVideo?.name}</div>
                <div className="mt-1 text-xs text-white/50">
                  آموزش اجرای حرکت
                </div>
              </div>

              <button
                onClick={() => setVideoModal(false)}
                className="text-2xl text-white/60 hover:text-white"
              >
                ×
              </button>
            </div>

{selectedVideo?.videoUrl ? (
  isGif(selectedVideo.videoUrl) ? (
    <img
      src={selectedVideo.videoUrl}
      alt={selectedVideo?.name || "exercise"}
      className="w-full bg-black object-contain"
    />
  ) : (
    <video
      src={selectedVideo.videoUrl}
      controls
      autoPlay
      playsInline
      className="aspect-video w-full bg-black"
    />
  )
) : (
  <div className="p-6 text-center text-sm text-white/60">
    ویدئوی این حرکت هنوز ثبت نشده.
  </div>
)}
          </div>
        </div>
      )}
    </Shell>
  );
}
function isGif(url) {
  return String(url || "").toLowerCase().split("?")[0].endsWith(".gif");
}
function Shell({ children }) {
  return (
    <main dir="rtl" className="font-vazir relative min-h-screen overflow-hidden bg-[#030712] text-white">
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(16,185,129,.32),transparent_35%),radial-gradient(circle_at_0%_85%,rgba(37,99,235,.28),transparent_38%),radial-gradient(circle_at_100%_70%,rgba(34,211,238,.16),transparent_30%)]" />
      <div className="fixed inset-0 opacity-[.15] bg-[linear-gradient(rgba(255,255,255,.08)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.08)_1px,transparent_1px)] bg-[size:34px_34px]" />
      <div className="fixed inset-0 bg-gradient-to-b from-transparent via-[#030712]/35 to-[#030712]" />
      {children}
    </main>
  );
}

function WorkoutPreview({ workoutDay, onPlay }) {
  const exercises = workoutDay?.exercises || [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-5 rounded-[30px] border border-white/10 bg-white/[.055] p-5 backdrop-blur-2xl"
    >
      <div className="flex items-center gap-2 text-emerald-200">
        <Dumbbell size={21} />
        <h2 className="font-black">
          {workoutDay.dayLabel || "نمونه تمرین روز اول"}
        </h2>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2">
        <Mini label="تمرکز" value={workoutDay.focus || "-"} />
        <Mini label="سطح" value={workoutDay.difficulty || "-"} />
        <Mini label="زمان" value={workoutDay.estimatedDuration || "-"} />
      </div>

      <div className="mt-5 space-y-3">
        {exercises.slice(0, 4).map((x, i) => (
          <ExerciseRow
    key={`${x.name}-${i}`}
    index={x.order || i + 1}
    exercise={x}
    onPlay={onPlay}
/>
        ))}
      </div>

      {(workoutDay.coachNotes || []).length > 0 && (
        <div className="mt-4 rounded-[24px] border border-emerald-400/20 bg-emerald-400/10 p-4">
          <div className="text-sm font-black text-emerald-100">نکته مربی</div>

          <div className="mt-3 space-y-2">
            {workoutDay.coachNotes.slice(0, 2).map((note) => (
              <div key={note} className="flex gap-2 text-xs leading-6 text-white/65">
                <Check size={15} className="mt-1 shrink-0 text-emerald-300" />
                {note}
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
}

function MealPreview({ nutritionDay }) {
  const firstMeal = nutritionDay?.meals?.[0];

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.08 }}
      className="mt-5 rounded-[30px] border border-white/10 bg-white/[.055] p-5 backdrop-blur-2xl"
    >
      <div className="flex items-center gap-2 text-cyan-200">
        <Utensils size={21} />
        <h2 className="font-black">
          {nutritionDay.dayLabel || "نمونه تغذیه پیشنهادی"}
        </h2>
      </div>

      <div className="mt-3 rounded-2xl border border-cyan-400/20 bg-cyan-400/10 px-4 py-3 text-xs font-bold text-cyan-100">
        کالری پیشنهادی: {nutritionDay.calorieRange || "-"}
      </div>

      {firstMeal && (
        <div className="mt-5 rounded-[26px] border border-cyan-400/20 bg-cyan-400/10 p-4">
          <div className="flex items-center gap-3">
            <div className="grid h-11 w-11 place-items-center rounded-2xl bg-cyan-300 text-black">
              <Salad size={23} />
            </div>

            <div>
              <div className="font-black">{firstMeal.mealType}</div>
              <div className="mt-1 text-xs text-white/55">
                بخشی از برنامه غذایی اختصاصی تو
              </div>
            </div>
          </div>

          <div className="mt-4 space-y-2">
            {(firstMeal.items || []).map((item, index) => (
              <div key={`${item.name}-${index}`} className="flex items-center gap-2 text-sm text-white/75">
                <Check size={15} className="shrink-0 text-cyan-300" />
                <span>
                  {item.name}
                  {item.amount ? ` - ${item.amount}` : ""}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {(nutritionDay.notes || []).length > 0 && (
        <div className="mt-4 space-y-2">
          {nutritionDay.notes.slice(0, 2).map((note) => (
            <div key={note} className="flex gap-2 rounded-[20px] border border-white/10 bg-black/25 p-3 text-xs leading-6 text-white/62">
              <Check size={15} className="mt-1 shrink-0 text-cyan-300" />
              {note}
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
}

function ExerciseRow({ index, exercise, onPlay }){
  const setText = [
    exercise.sets,
    exercise.reps,
    exercise.rest ? `استراحت ${exercise.rest}` : null,
  ]
    .filter(Boolean)
    .join(" | ");

  return (
    <div className="flex items-center gap-3 rounded-[24px] border border-white/10 bg-black/25 p-3">
      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-emerald-300 font-black text-black">
        {index}
      </div>

      <div className="flex-1">
        <div className="font-black">{exercise.name}</div>
        <div className="mt-1 text-xs text-white/50">{setText}</div>

        {exercise.technique && (
          <div className="mt-2 text-[11px] leading-5 text-emerald-100/70">
            {exercise.technique}
          </div>
        )}
      </div>

      <button
    onClick={() => onPlay(exercise)}
    className="transition hover:scale-110"
>
    <PlayCircle
        size={24}
        className="text-emerald-300"
    />
</button>
    </div>
  );
}

function LockedValue({ lockedSections, disclaimer }) {
  const sections = lockedSections?.length
    ? lockedSections
    : [
        {
          title: "نسخه کامل قفله",
          description: "برنامه کامل شامل تمرین، رژیم، ویدئوها و امکان اصلاح تا ۲۴ ساعت بعد از خریده",
        },
      ];

  return (
    <div className="mt-5 rounded-[30px] border border-amber-300/20 bg-amber-300/10 p-5 text-center backdrop-blur-2xl">
      <div className="mx-auto grid h-12 w-13 place-items-center rounded-2xl bg-amber-300 text-black">
        <Lock size={30} />
      </div>

      <h3 className="mt-4 text-lg font-black">بخش‌های قفل‌شده برنامه</h3>

      <div className="mt-4 space-y-3 text-right">
        {sections.slice(0, 4).map((section, index) => (
          <LockedRow
            key={section.key || `${section.title}-${index}`}
            title={section.title}
            description={section.description}
          />
        ))}
      </div>

      {disclaimer && (
        <p className="mt-4 text-[11px] leading-6 text-white/45">
          {disclaimer}
        </p>
      )}
    </div>
  );
}

function LockedRow({ title, description }) {
  return (
    <div className="relative overflow-hidden rounded-[24px] border border-white/10 bg-white/[.035] p-4">
      <div className="absolute inset-0 backdrop-blur-[2px]" />

      <div className="relative flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-bold text-white/65">{title}</div>
          {description && (
            <div className="mt-1 text-xs leading-5 text-white/42">{description}</div>
          )}
        </div>

        <div className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-white/8 text-white/50">
          <Lock size={16} />
        </div>
      </div>
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/25 p-3 text-center">
      <div className="text-[10px] text-white/40">{label}</div>
      <div className="mt-1 truncate text-xs font-black text-white">{value || "-"}</div>
    </div>
  );
}

function safeJsonParse(value) {
  try {
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
}